<template>
  <div class="chromakey-captions" :class="{ 'chromakey-captions--interactive': interactive, 'chromakey-captions--dragging': dragging }"
       :style="{ textAlign: align, fontFamily }">
    <!-- Green Screen captions: a fixed-height window onto the text, sized to the line count. The
         track inside is pinned to the window's bottom, so the browser wraps the text at the real
         width and older lines slide up out of view. Used by the room's Green Screen view and is
         meant to be shared with the OBS overlay, so both draw the captions the same way. -->
    <!-- While dragging, a faded copy of the captions outside the window shows the lines the
         line count hides. It wraps exactly like the real track (same width, same bottom
         anchor), and the window paints over it, so only the hidden lines show. -->
    <div
        v-if="dragging"
        class="chromakey-track chromakey-track--ghost"
        :class="{ 'chromakey-track--sliding': sliding }"
        :style="{ transform: `translateY(${slideOffset}px)` }"
        aria-hidden="true"
    >
      <span class="chromakey-caption">{{ text }}</span>
    </div>

    <div class="chromakey-viewport">
      <div
          ref="track"
          class="chromakey-track"
          :class="{ 'chromakey-track--sliding': sliding }"
          :style="{ transform: `translateY(${slideOffset}px)` }"
      >
        <span class="chromakey-caption">{{ text }}</span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, defineEmits, defineExpose, defineProps, nextTick, onMounted, ref, watch } from 'vue';

const props = defineProps({
  text: {
    type: String,
    required: true,
  },
  // In px.
  fontSize: {
    type: Number,
    required: true,
  },
  fontFamily: {
    type: String,
    required: false,
    default: 'Inter',
  },
  lines: {
    type: Number,
    required: true,
  },
  align: {
    type: String,
    required: false,
    default: 'center',
  },
  // The room lets the captions be dragged into place; the overlay only shows them.
  interactive: Boolean,
  // Shows the drag box and the lines the line count hides.
  dragging: Boolean,
})

// layout: the captions have re-measured and may have changed size, e.g. to re-check where
// they may go.
const emit = defineEmits(['layout'])

const track = ref(null)

// Slide-in of a new line, and the measured metrics the window height is derived from.
const slideOffset = ref(0)
const sliding = ref(false)
const trackHeight = ref(0)
const pillOverhang = ref(1)
const lineBox = ref(0)

const lineHeight = computed(() => {
  // Measured once the captions have rendered; the ratio matches the 150% line-height
  // and only stands in until then.
  return lineBox.value || props.fontSize * 1.5
})

const bottomGutter = computed(() => {
  // The pill is taller than its line box — that overlap is what keeps consecutive lines
  // touching — so it hangs below the last line by ~0.12em and would be shaved without
  // this slack. Nothing is reserved above on purpose: headroom there would expose a
  // sliced strip of the line that just scrolled out instead of cutting cleanly along
  // the line boundary. The floor covers the small sizes, where the pill's rem padding
  // floor makes the overhang bigger than any share of the font size would suggest.
  return Math.ceil(Math.max(10, props.fontSize * 0.14))
})
const bottomGutterPx = computed(() => `${bottomGutter.value}px`)

const viewportHeight = computed(() => {
  // Rounded down: the measured line box is fractional, and rounding up would lift the
  // window's top edge into the pill of the line above by up to a pixel.
  const linesHeight = Math.floor(lineHeight.value * props.lines)
  // Trimmed by the pill overhang so the line that just scrolled out is clipped whole:
  // its pill hangs below its own line box, and any of it left inside the window paints
  // as a black bar wherever that line was wider than the one now on top. The extra pixel
  // keeps the clip edge off the pill's edge, which at fractional device pixel ratios
  // (125%/150% Windows scaling) is antialiased into a faint line even when they coincide.
  return `${linesHeight + bottomGutter.value - pillOverhang.value - 1}px`
})

const fontSizePx = computed(() => `${props.fontSize}px`)

const measureMetrics = () => {
  const trackEl = track.value
  const caption = trackEl && trackEl.firstElementChild
  if (!caption) return

  const rects = caption.getClientRects()
  if (!rects.length) return

  // Client rects come out on screen, after any scale the room puts on the captions to fit
  // its stage, while the window height is set in layout pixels: measure the scale and undo it.
  const trackRect = trackEl.getBoundingClientRect()
  const scale = trackEl.offsetHeight > 0 ? trackRect.height / trackEl.offsetHeight : 1

  // Client rects of an inline box are padding boxes, so the last one reaching past the
  // track — whose height is exactly its line boxes — measures how far a pill spills
  // below the line it belongs to. Depends on the font's metrics, so it is measured
  // rather than derived.
  const overhang = Math.max(1, Math.ceil((rects[rects.length - 1].bottom - trackRect.bottom) / scale))
  if (overhang !== pillOverhang.value) {
    pillOverhang.value = overhang
  }

  if (rects.length > 1) {
    // The real line box is not reliably font-size * 1.5: browsers clamp font sizes below
    // their configured minimum, and fractional sizes round per line. Deriving the window
    // height from the rendered value keeps it correct in both cases.
    const measured = (rects[rects.length - 1].top - rects[0].top) / (rects.length - 1) / scale
    if (measured > 0 && measured !== lineBox.value) {
      lineBox.value = measured
    }
  }
}

// Forgets the last height, so the next update places the text without sliding: for changes
// that rewrap everything (size, width, line count) rather than add a line.
const reset = () => {
  sliding.value = false
  slideOffset.value = 0
  trackHeight.value = 0
}

const place = () => {
  const trackEl = track.value
  if (!trackEl) {
    trackHeight.value = 0
    return
  }

  measureMetrics()

  const height = trackEl.offsetHeight
  const previousHeight = trackHeight.value
  trackHeight.value = height

  // The track is pinned to the bottom of the window, so gaining a line pushes everything
  // up by that line's height in one frame. Only that growth needs animating: put the text
  // back where it just was, then let it travel to its new place.
  if (!previousHeight || height <= previousHeight) {
    sliding.value = false
    slideOffset.value = 0
    return
  }

  sliding.value = false
  slideOffset.value = height - previousHeight

  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      if (!track.value) return
      sliding.value = true
      slideOffset.value = 0
    })
  })
}

const update = () => {
  place()
  // After the window has re-rendered at its new height.
  nextTick(() => emit('layout'))
}

watch(() => props.text, () => nextTick(update))
watch(() => props.fontSize, () => {
  reset()
  nextTick(update)
})
watch(() => props.lines, reset)
// Different metrics, so the pill spills past its line box by a different amount.
watch(() => props.fontFamily, () => nextTick(update))

onMounted(() => nextTick(update))

defineExpose({ reset, update })
</script>

<style scoped>
.chromakey-captions {
  /* Anchors the drag-time ghost track to the window's box. */
  position: relative;
  width: 100%;
}

.chromakey-viewport {
  position: relative;
  width: 100%;
  height: v-bind(viewportHeight);
  overflow: hidden;
}

.chromakey-captions--interactive {
  cursor: grab;
  /* Dragged with a finger too, so the browser must not scroll or zoom on it. */
  touch-action: none;
  user-select: none;
  -webkit-user-select: none;
  -webkit-touch-callout: none;
}

.chromakey-captions--dragging {
  cursor: grabbing;
}

/* Marks the box the drag is limited by. Dashed dark over a light inset line, so it reads on
   any background color. */
.chromakey-captions--dragging .chromakey-viewport {
  outline: 2px dashed rgba(0, 0, 0, 0.6);
  outline-offset: -2px;
  box-shadow: inset 0 0 0 2px rgba(255, 255, 255, 0.6);
}

.chromakey-track {
  position: absolute;
  left: 0;
  right: 0;
  bottom: v-bind(bottomGutterPx);
  padding: 0 1rem;
  will-change: transform;
}

.chromakey-track--ghost {
  opacity: 0.4;
  pointer-events: none;
}

.chromakey-track--sliding {
  transition: transform 240ms cubic-bezier(0.22, 0.61, 0.36, 1);
}

.chromakey-caption {
  background: #000000;
  /* Vertical padding has to scale with the text: line-height is 150%, so a fixed rem
     stops covering the line box past ~48px and green shows through between lines.
     The rem floor keeps the smaller sizes looking exactly as they did. */
  padding: max(0.3rem, 0.16em) 1rem;
  color: #FFFFFF;
  display: inline;
  box-decoration-break: clone;
  -webkit-box-decoration-break: clone;
  font-weight: 400;
  font-size: v-bind(fontSizePx);
  line-height: 150%;
  word-break: break-word;
  white-space: pre-line;
}

@media (prefers-reduced-motion: reduce) {
  .chromakey-track--sliding {
    transition: none;
  }
}
</style>
